import { AiConsentGate } from '../ui/AiConsentGate';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { api, hasSidebar, labelForView, viewForPath, type FacilityChecklist, type NavBadgesResponse } from '@aithora/shared';
import { useConsoleRole, useSession } from '../session/SessionContext';
import { useQuery, type QueryState } from '../useQuery';
import { SideNav } from './SideNav';
import { TopBar, atlasAllowed } from './TopBar';
import { CHECKLIST_PREFS_EVENT, FirstTimeBanner } from './FirstTimeBanner';
import { EmergencyBanner } from './EmergencyBanner';
import { ResumeLine } from './ResumeLine';
import { TestPageBanner } from './TestPageBanner';
import { FullConsoleNotice } from './FullConsoleNotice';
import { AtlasPanel } from './AtlasPanel';
import { CommandPalette } from './CommandPalette';
import { HelpDrawer } from './HelpDrawer';
import { ViewAsBanner } from './ViewAsPicker';
import { AiControlsProvider } from './aiControls';
import { AiPausedBar, AiPauseSheet } from './AiStatus';
import { DeleteAccountDialog, useDeleteAccountDialog } from './DeleteAccountDialog';
import { DemoScopeProvider } from '../session/DemoScope';
import { FacilityScopeProvider } from '../session/FacilityScope';

/** Poll the menu labels this often until the live socket exists. */
export const BADGE_POLL_MS = 30_000;
const COLLAPSE_KEY = 'aithora.console.navCollapsed';
/** Under this viewport width the design switches to its phone frame (design: phone, < 760). */
export const PHONE_MAX_WIDTH = 760;
/** At this console width and wider, the open Help drawer docks as a third column (design: dock, >= 1240). */
export const HELP_DOCK_MIN_WIDTH = 1240;

const BadgesCtx = createContext<QueryState<NavBadgesResponse> | null>(null);
/** The shell's badge data, for pages that want the same figures (Overview). */
export function useNavBadges(): QueryState<NavBadgesResponse> {
  const v = useContext(BadgesCtx);
  if (!v) throw new Error('useNavBadges must be used inside the Shell');
  return v;
}

/**
 * The label of the tab a page is showing, so Help can ask about it ("What is the Care
 * Plans tab on Residents for?"). A page calls usePageTab('Care Plans').
 */
const PageTabCtx = createContext<(label: string | null) => void>(() => {});
export function usePageTab(label: string | null) {
  const set = useContext(PageTabCtx);
  useEffect(() => {
    set(label);
    return () => set(null);
  }, [label, set]);
}

/** What pages can ask of the shell (useFlushContent, useOpenHelp, useAtlasSubject). */
interface ShellApi {
  setFlush: (on: boolean) => void;
  openHelp: (question?: string) => void;
  setAtlasSubject: (name: string | null) => void;
}
const ShellApiCtx = createContext<ShellApi>({ setFlush: () => {}, openHelp: () => {}, setAtlasSubject: () => {} });

/**
 * The page fills #console-content edge to edge (padding 0), as the design does for
 * Atlas (contentPad, L39401). Call it at the top of the page; it is undone when the
 * page unmounts or `on` turns false.
 */
export function useFlushContent(on = true) {
  const { setFlush } = useContext(ShellApiCtx);
  useEffect(() => {
    if (!on) return;
    setFlush(true);
    return () => setFlush(false);
  }, [on, setFlush]);
}

/**
 * Opens the Help drawer; with a question, the drawer asks it at once, exactly as if it
 * had been typed there (POST /help/ask, then the help text). The design's Help page hands
 * its search to the drawer this way (hdQKey).
 */
export function useOpenHelp(): (question?: string) => void {
  return useContext(ShellApiCtx).openHelp;
}

/**
 * The page's subject (a resident's name), shown on the top bar's Atlas button while the
 * page is open (design atlasSubject, L38367). Pass null for none.
 */
export function useAtlasSubject(name: string | null) {
  const { setAtlasSubject } = useContext(ShellApiCtx);
  useEffect(() => {
    setAtlasSubject(name);
    return () => setAtlasSubject(null);
  }, [name, setAtlasSubject]);
}

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
}

const isPhoneWidth = () => typeof window !== 'undefined' && window.innerWidth < PHONE_MAX_WIDTH;

export function Shell() {
  const { session, client, signOut } = useSession();
  const { role, realRole, viewingAs } = useConsoleRole();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState<boolean>(readCollapsed);
  const [phone, setPhone] = useState<boolean>(isPhoneWidth);
  const [phoneNav, setPhoneNav] = useState(false);
  const [shellW, setShellW] = useState<number | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [atlasOpen, setAtlasOpen] = useState(false);
  const [tabLabel, setTabLabel] = useState<string | null>(null);
  const [flush, setFlush] = useState(false);
  const [atlasSubject, setAtlasSubject] = useState<string | null>(null);
  /** A question handed to the Help drawer; `n` makes the same question ask again. */
  const [helpAsk, setHelpAsk] = useState<{ q: string; n: number } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const shellApi = useMemo<ShellApi>(
    () => ({
      setFlush,
      setAtlasSubject,
      openHelp: (question?: string) => {
        setHelpOpen(true);
        setAtlasOpen(false);
        const q = question?.trim();
        if (q) setHelpAsk((p) => ({ q, n: (p?.n ?? 0) + 1 }));
      },
    }),
    [],
  );

  const badges = useQuery<NavBadgesResponse>((signal) => api.navBadges(client, { signal }), [client, session?.token, viewingAs], {
    pollMs: BADGE_POLL_MS,
    enabled: !!session,
  });
  const orgId = session?.user.organizationId ?? null;
  const checklist = useQuery<FacilityChecklist>((signal) => api.facilityChecklist(client, { params: { id: orgId! }, signal }), [client, orgId], {
    enabled: role === 'ADMIN' && !!orgId,
  });
  // The Overview hides or folds the checklist (PUT .../prefs); keep this copy in step so
  // the first-time banner appears the moment the checklist is hidden (design: obDismissed).
  const setChecklist = checklist.setData;
  useEffect(() => {
    const on = (e: Event) => {
      const d = (e as CustomEvent<{ dismissed: boolean; collapsed: boolean }>).detail;
      setChecklist((prev) => (prev ? { ...prev, dismissed: d.dismissed, collapsed: d.collapsed } : prev));
    };
    window.addEventListener(CHECKLIST_PREFS_EVENT, on);
    return () => window.removeEventListener(CHECKLIST_PREFS_EVENT, on);
  }, [setChecklist]);
  const closeAtlas = useCallback(() => setAtlasOpen(false), []);
  // AI status's "waiting for your OK in Atlas" opens the panel on its queue.
  const openQueue = useCallback(() => {
    setAtlasOpen(true);
    setHelpOpen(false);
  }, []);
  const del = useDeleteAccountDialog();
  const closeHelp = useCallback(() => setHelpOpen(false), []);
  // A handed-over question is asked once: closing the drawer, however, forgets it.
  useEffect(() => {
    if (!helpOpen) setHelpAsk(null);
  }, [helpOpen]);
  const closePalette = useCallback(() => setPaletteOpen(false), []);

  // The console's width (for docking Help) and the phone frame, kept current on resize.
  useEffect(() => {
    const measure = () => {
      setPhone(isPhoneWidth());
      if (rootRef.current) setShellW(rootRef.current.clientWidth);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  useEffect(() => {
    if (!phone) setPhoneNav(false);
  }, [phone]);

  // Keyboard (design _cpKey, L18225): ? opens Help, Escape closes it, Cmd or Ctrl+K opens
  // the command palette. The palette handles its own arrows, Enter and Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const typing = !!t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
      if (e.key === '?' && !typing && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        setHelpOpen(true);
        setAtlasOpen(false);
        return;
      }
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // A page change is when a nurse most expects the menu to be current (the first
  // render already loads, so skip it). It also closes the phone drawer.
  const firstPath = useRef(true);
  useEffect(() => {
    setPhoneNav(false);
    if (firstPath.current) {
      firstPath.current = false;
      return;
    }
    badges.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggle = () => {
    setCollapsed((c) => {
      const next = !c;
      try {
        window.localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
      } catch {
        /* a convenience only */
      }
      return next;
    });
  };

  if (!session) return null;
  const view = viewForPath(location.pathname);
  const crumb = view ? labelForView(view) : 'Console';
  const sidebar = hasSidebar(role);
  // A Facility Admin's banner waits until their setup checklist is dismissed; while the
  // answer is unknown, treat the checklist as visible so the two never stack.
  const checklistVisible = role === 'ADMIN' ? !checklist.data || !checklist.data.dismissed : false;
  const dock = helpOpen && !phone && (shellW ?? 0) >= HELP_DOCK_MIN_WIDTH;
  const cls = ['shell', sidebar ? '' : 'shell--nosidebar', collapsed && !phone ? 'shell--collapsed' : '', phone ? 'shell--phone' : '', phoneNav ? 'shell--navopen' : '', dock ? 'shell--dock' : '']
    .filter(Boolean)
    .join(' ');

  // The facility switcher is for Operator and Super Admin signed in as themselves (not while
  // viewing as another role), and not on a phone (design scopeLayer).
  const switcher = !phone && !viewingAs && (role === 'SUPER_ADMIN' || role === 'OPERATOR' || (role as string) === 'OWNER');

  return (
    <FacilityScopeProvider enabled={switcher}>
    <BadgesCtx.Provider value={badges}>
      <PageTabCtx.Provider value={setTabLabel}>
      <ShellApiCtx.Provider value={shellApi}>
      <AiControlsProvider onOpenQueue={openQueue}>
        <a href="#console-content" className="skiplink">
          Skip to content
        </a>
        <div ref={rootRef} className={cls}>
          {sidebar && (
            <SideNav
              role={role}
              badges={badges.data}
              collapsed={collapsed && !phone}
              onToggleCollapsed={toggle}
              phone={phone}
              open={phoneNav}
              onSignOut={signOut}
              onNavigate={() => setPhoneNav(false)}
            />
          )}
          {phone && phoneNav && <button type="button" className="shell__scrim" aria-label="Close menu" onClick={() => setPhoneNav(false)} />}
          <div className="shell__main">
            <TopBar
              crumb={crumb}
              session={session}
              role={role}
              realRole={realRole}
              viewingAs={viewingAs}
              onSignOut={signOut}
              atlasOpen={atlasOpen}
              atlasSubject={atlasSubject}
              onToggleAtlas={() => {
                setAtlasOpen((o) => !o);
                setHelpOpen(false);
              }}
              helpOpen={helpOpen}
              onToggleHelp={() => {
                setHelpOpen((o) => !o);
                setAtlasOpen(false);
              }}
              phone={phone}
              narrow={shellW !== null && shellW < 950}
              wide={shellW === null || shellW >= 1180}
              path={location.pathname}
              onToggleMenu={sidebar ? () => setPhoneNav((o) => !o) : undefined}
              onDeleteAccount={del.open}
            />
            <AiPausedBar />
            <main id="console-content" className={`content${flush ? ' content--flush' : ''}`} tabIndex={-1}>
              <FullConsoleNotice />
              <ViewAsBanner viewingAs={viewingAs} />
              <EmergencyBanner role={role} view={view} />
              {/* V9: small, right under the emergency line, only on the page it names. */}
              <ResumeLine />
              <TestPageBanner />
              <FirstTimeBanner role={role} view={view} checklistVisible={checklistVisible} />
              <DemoScopeProvider demo={session.user.demoBuilding === true}>
                <div className="content__page">
                  <Outlet />
                </div>
              </DemoScopeProvider>
            </main>
          </div>
        </div>
        {atlasOpen && atlasAllowed(role) && (role === 'FAMILY_MEMBER' ? (
          // Family: the AI consent notice first, as on the phone (the same server record).
          <AiConsentGate panel onClose={closeAtlas}><AtlasPanel view={view} onClose={closeAtlas} /></AiConsentGate>
        ) : <AtlasPanel view={view} onClose={closeAtlas} />)}
        {helpOpen && <HelpDrawer view={view} tabLabel={tabLabel} ask={helpAsk} onClose={closeHelp} />}
        {paletteOpen && <CommandPalette onClose={closePalette} />}
        <AiPauseSheet />
        {del.isOpen && <DeleteAccountDialog onClose={del.close} />}
      </AiControlsProvider>
      </ShellApiCtx.Provider>
      </PageTabCtx.Provider>
    </BadgesCtx.Provider>
    </FacilityScopeProvider>
  );
}
